import React, { useEffect, useRef } from "react";
import { MonitorCheck, ClipboardCheck, ChartNoAxesCombined } from "lucide-react";
import { useTranslation } from "react-i18next";
import "./OpenLiftIntro.css";

/**
 * OPENlift — Section 02 : bénéfices métier
 * Le nom exporté OpenLiftIntro est conservé pour la page OPENlift existante.
 * Les traductions utilisent des clés dédiées et un repli FR/EN sans
 * modifier les fichiers i18n globaux du projet.
 */
const CONTENT = {
  fr: {
    brand: "OPENlift",
    section: "CE QUE VOUS GAGNEZ",
    eyebrow: "Vos équipes. Vos priorités. Votre parc.",
    titleLine1: "Moins d’incertitude.",
    titleLine2: "Plus d’efficacité sur le terrain.",
    subtitle: "Une information utile n’a de valeur que si vos équipes peuvent agir au bon moment.",
    cards: [
      {
        tag: "01 / OBSERVER",
        title: "Voyez ce qui se passe",
        description: "Consultez l’état et l’historique disponibles de vos ascenseurs sans attendre un déplacement.",
        outcome: "Vision du parc"
      },
      {
        tag: "02 / PRÉPARER",
        title: "Préparez mieux les interventions",
        description: "Accédez aux défauts et événements utiles pour orienter les premières vérifications avant l’arrivée sur site.",
        outcome: "Aide au diagnostic"
      },
      {
        tag: "03 / PILOTER",
        title: "Pilotez votre activité",
        description: "Regroupez vos informations dans un seul espace pour suivre vos équipements et mieux prioriser les actions.",
        outcome: "Suivi opérationnel"
      }
    ]
  },
  en: {
    brand: "OPENlift",
    section: "WHAT YOU GAIN",
    eyebrow: "Your teams. Your priorities. Your fleet.",
    titleLine1: "Less uncertainty.",
    titleLine2: "Greater efficiency in the field.",
    subtitle: "Useful information only creates value when your teams can act at the right time.",
    cards: [
      {
        tag: "01 / MONITOR",
        title: "See what is happening",
        description: "Check available elevator status and history without waiting for an on-site visit.",
        outcome: "Fleet visibility"
      },
      {
        tag: "02 / PREPARE",
        title: "Prepare interventions better",
        description: "Access useful faults and events to guide initial checks before arriving on site.",
        outcome: "Diagnostic support"
      },
      {
        tag: "03 / MANAGE",
        title: "Manage your operations",
        description: "Bring information together in one place to monitor equipment and prioritize actions.",
        outcome: "Operational oversight"
      }
    ]
  }
};

/** Icônes Lucide — mêmes 3 cartes et même API BenefitIcon. */
const BENEFIT_ICONS = {
  observe: MonitorCheck,
  prepare: ClipboardCheck,
  manage: ChartNoAxesCombined
};

function BenefitIcon({ type }) {
  const Icon = BENEFIT_ICONS[type] || ChartNoAxesCombined;
  return <Icon size={37} strokeWidth={2.1} aria-hidden="true" focusable="false" />;
}

const ICONS = ["observe", "prepare", "manage"];

export default function OpenLiftIntro() {
  const { t, i18n } = useTranslation();
  const rootRef = useRef(null);
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const fallback = CONTENT[language] || CONTENT.fr;
  const read = (key, defaultValue) =>
    t(`openliftBenefits.premium.${key}`, { defaultValue });

  const cards = fallback.cards.map((card, index) => ({
    tag: read(`cards.${index}.tag`, card.tag),
    title: read(`cards.${index}.title`, card.title),
    description: read(`cards.${index}.description`, card.description),
    outcome: read(`cards.${index}.outcome`, card.outcome),
    icon: ICONS[index]
  }));

  useEffect(() => {
    const element = rootRef.current;
    if (!element || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          element.classList.add("openlift-benefits--revealed");
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="openlift-benefits"
      id="openlift-benefits"
      ref={rootRef}
      aria-labelledby="ol-intro-title"
    >
      <div className="openlift-benefits__container">
        <div className="openlift-benefits__topline" aria-hidden="true">
          <span>{read("brand", fallback.brand)}</span>
          <span>{read("section", fallback.section)}</span>
        </div>

        <header className="openlift-benefits__header">
          <p className="openlift-benefits__eyebrow">
            <span className="openlift-benefits__eyebrow-line" aria-hidden="true" />
            {read("eyebrow", fallback.eyebrow)}
          </p>
          <h2 id="ol-intro-title" className="openlift-benefits__title">
            <span>{read("titleLine1", fallback.titleLine1)}</span>
            <span className="openlift-benefits__title-accent">
              {read("titleLine2", fallback.titleLine2)}
            </span>
          </h2>
          <p className="openlift-benefits__subtitle">
            {read("subtitle", fallback.subtitle)}
          </p>
        </header>

        <div className="openlift-benefits__grid">
          {cards.map((card, index) => (
            <article className="openlift-benefits__card" key={card.icon}>
              <p className="openlift-benefits__number">{card.tag}</p>
              <div className="openlift-benefits__icon" aria-hidden="true">
                <BenefitIcon type={card.icon} />
              </div>
              <h3 className="openlift-benefits__card-title">{card.title}</h3>
              <p className="openlift-benefits__description">{card.description}</p>
              {/* Libellé métier informatif : ne simule pas un lien cliquable. */}
              <p className="openlift-benefits__outcome">{card.outcome}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
